import React, { useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import { RouteParam } from '../../../core/routing/param';
import { BARCODE_SCAN_PATH } from '../constants';

/**
 * This component is responsible to ensure that after using the scanner, all artifacts are removed from memory by doing a page reload.
 * Scandit does not provide a trivial way to reset instances and appears to have no example where such a reset is implemented reliably.
 * Checked resources: https://github.com/Scandit/datacapture-web-samples and especially https://github.com/Scandit/datacapture-web-samples/blob/master/BarcodeCaptureReactSample/src/sdk.tsx .
 * The React sample contains a cleanup method, but this does not appear to be sufficient. In addition, it's not actively used in their sample as the context is not unmounted.
 *
 * This anti-pattern is used to ensure to cleanup any resources because of other features that are using rather demanding resources.
 * This should not be taken as a reference for the future.
 * @constructor
 */
export const ScannerBruteforceMemoryReset: React.FC = () => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  // Detect if this component is going to unmount due to a page navigation
  useEffect(() => {
    const hasNavigated = location.pathname !== pathnameRef.current;

    if (hasNavigated) {
      const wasOnScannerPage = matchPath(`:${RouteParam.lang}/${BARCODE_SCAN_PATH}`, pathnameRef.current);
      if (wasOnScannerPage) {
        window.location.reload();
      }
    }

    pathnameRef.current = location.pathname;
  }, [location]);
  return null;
};
